import type { SupabaseClient } from '@supabase/supabase-js'
import { readSessionIdentity } from './accessToken'
import { supabaseForToken } from './supabaseScoped'
import { supabase } from './supabase'

// The account's saved_collections row (its cloud collection pointer), read and
// written ONLY as a verified account.
//
// Every call reads the CURRENT session, requires it to be `ownerId`, and uses
// THAT session's token for the whole call through a pinned client. If the
// session's user changed by the time the call finishes, the result is dropped
// (a read is not used, a write reports failure): nothing is ever adopted into,
// or reported for, a different account than the one the call started as, and
// no request can go out as anyone else.

export interface SavedCollectionRow {
  collection_id: string | null
  card_count: number | null
  source_url: string | null
}

type AsOwner<T> = { ok: true; value: T } | { ok: false }

async function asOwner<T>(ownerId: string, fn: (client: SupabaseClient) => Promise<T>): Promise<AsOwner<T>> {
  const live = await readSessionIdentity({ fresh: true })
  if (!live.ok || live.userId !== ownerId || !live.token) return { ok: false }
  // The pinned client exists whenever Supabase is configured. The shared
  // client is the fallback only when it is not (then it is null as well,
  // outside tests); the identity check after the call still applies.
  const client = supabaseForToken(live.token) ?? supabase
  if (!client) return { ok: false }
  let value: T
  try {
    value = await fn(client)
  } catch {
    return { ok: false }
  }
  const after = await readSessionIdentity()
  if (!after.ok || after.userId !== ownerId) return { ok: false }
  return { ok: true, value }
}

/** The account's row: null when it has none; ok:false when it could not be read as that account. */
export async function readSavedCollection(
  ownerId: string,
): Promise<{ ok: true; row: SavedCollectionRow | null } | { ok: false }> {
  const r = await asOwner(ownerId, async (client) => {
    const { data, error } = await client
      // eslint-disable-next-line i18next/no-literal-string -- table name, never shown
      .from('saved_collections')
      // eslint-disable-next-line i18next/no-literal-string -- column names, never shown
      .select('collection_id, source_url, card_count')
      // eslint-disable-next-line i18next/no-literal-string -- column name, never shown
      .eq('user_id', ownerId)
      .limit(1)
    if (error) throw new Error(error.message)
    const rows = (data ?? []) as Partial<SavedCollectionRow>[]
    if (rows.length === 0) return null
    return {
      collection_id: rows[0].collection_id ?? null,
      card_count: rows[0].card_count ?? null,
      source_url: rows[0].source_url ?? null,
    }
  })
  return r.ok ? { ok: true, row: r.value } : { ok: false }
}

/** Point the account's row at a collection. True only when it landed for that account. */
export async function upsertSavedCollection(ownerId: string, row: SavedCollectionRow): Promise<boolean> {
  const r = await asOwner(ownerId, async (client) => {
    const { error } = await client
      // eslint-disable-next-line i18next/no-literal-string -- table name, never shown
      .from('saved_collections')
      .upsert({
        user_id: ownerId,
        collection_id: row.collection_id,
        card_count: row.card_count ?? 0,
        source_url: row.source_url,
      // eslint-disable-next-line i18next/no-literal-string -- upsert conflict column
      }, { onConflict: 'user_id' })
    if (error) throw new Error(error.message)
    return true
  })
  return r.ok
}

/**
 * Clear the account's row when (and only when) it still points at
 * `collectionId`: a pointer the account has dropped (a 403 said it belongs to
 * another account). Verified by reading the row back, since Supabase reports
 * an RLS-filtered delete as success. True when no row holds it any more.
 */
export async function clearSavedCollectionPointer(ownerId: string, collectionId: string): Promise<boolean> {
  const r = await asOwner(ownerId, async (client) => {
    const { error } = await client
      // eslint-disable-next-line i18next/no-literal-string -- table name, never shown
      .from('saved_collections')
      .delete()
      // eslint-disable-next-line i18next/no-literal-string -- column name, never shown
      .eq('user_id', ownerId)
      // eslint-disable-next-line i18next/no-literal-string -- column name, never shown
      .eq('collection_id', collectionId)
    if (error) throw new Error(error.message)
    const { data, error: readError } = await client
      // eslint-disable-next-line i18next/no-literal-string -- table name, never shown
      .from('saved_collections')
      // eslint-disable-next-line i18next/no-literal-string -- column name, never shown
      .select('collection_id')
      // eslint-disable-next-line i18next/no-literal-string -- column name, never shown
      .eq('user_id', ownerId)
      .limit(1)
    if (readError) throw new Error(readError.message)
    const rows = (data ?? []) as { collection_id?: string | null }[]
    return !rows.some(x => x.collection_id === collectionId)
  })
  return r.ok && r.value
}
